import React, { useState } from 'react';
import { ChevronRight, FileText, Mail, Ticket, Calendar, CreditCard } from 'lucide-react';
import { SyncRun, SyncSource, SOURCES, STATUS_DOT, runsBySource, relativeTime, formatDuration } from './types';
interface Props {
  onSelectRun: (runId: string) => void;
}
const SOURCE_ICONS: Record<SyncSource, React.FC<{
  size?: number;
  className?: string;
}>> = {
  confluence: FileText,
  gmail: Mail,
  jira: Ticket,
  gcal: Calendar,
  quickbooks: CreditCard
};
export function SyncIndex({
  onSelectRun
}: Props) {
  const [showOlder, setShowOlder] = useState<Record<string, boolean>>({});
  const grouped = runsBySource();
  return <>
      <header className="mb-8" data-id="element-1938">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-1" data-id="element-1939">Sync History</h1>
        <p className="text-[#8a8680] text-[14px]" data-id="element-1940">Pipeline observability — latest Nango syncs, per source.</p>
      </header>

      {SOURCES.map(source => {
      const runs = grouped[source.id];
      const SourceIcon = SOURCE_ICONS[source.id];
      const visible = showOlder[source.id] ? runs : runs.slice(0, 3);
      const hasMore = runs.length > visible.length;
      return <section key={source.id} className="mb-10" data-id="element-1941">
            <div className="flex items-center justify-between mb-4" data-id="element-1942">
              <div className="flex items-center gap-3" data-id="element-1943">
                <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center" data-id="element-1944">
                  <SourceIcon size={18} className="text-[#8a8680]" data-id="element-1945" />
                </div>
                <div data-id="element-1946">
                  <h2 className="text-[15px] font-medium text-[#e8e4df]" data-id="element-1947">{source.label}</h2>
                  <p className="text-[12px] text-[#8a8680]/70" data-id="element-1948">
                    last sync · {runs[0] ? relativeTime(runs[0].startedAt) : 'never'} · {source.cadence}
                  </p>
                </div>
              </div>
              {runs[0] && <span className={`w-2 h-2 rounded-full ${STATUS_DOT[runs[0].status]}`} data-id="element-1949" />}
            </div>

            {runs.length === 0 ? <p className="text-[#8a8680]/50 text-[13px] pl-[52px]" data-id="element-1950">No syncs in the last 7 days</p> : <div className="flex flex-col gap-2" data-id="element-1951">
                {visible.map(run => <RunRow key={run.id} run={run} onClick={() => onSelectRun(run.id)} data-id="element-1952" />)}
              </div>}

            {hasMore && <button onClick={() => setShowOlder(prev => ({
          ...prev,
          [source.id]: true
        }))} className="mt-3 ml-[52px] text-[12px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-1953">
                Show older
              </button>}
          </section>;
    })}
    </>;
}
function RunRow({
  run,
  onClick
}: {
  run: SyncRun;
  onClick: () => void;
}) {
  const {
    counts,
    status,
    startedAt,
    durationMs
  } = run;
  return <button onClick={onClick} className="w-full text-left bg-[#1c1c22] rounded-[12px] px-4 py-3 hover:bg-[#1e1e25] transition-colors" data-id="element-1954">
      <div className="flex items-center gap-3" data-id="element-1955">
        <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]} shrink-0`} data-id="element-1956" />
        <span className="text-[13px] text-[#e8e4df] font-medium w-20 shrink-0" data-id="element-1957">{relativeTime(startedAt)}</span>
        <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-1958">
          {counts.new > 0 && <Pill text={`+${counts.new} new`} tone="teal" data-id="element-1959" />}
          {counts.updated > 0 && <Pill text={`~${counts.updated} updated`} tone="muted" data-id="element-1960" />}
          {counts.failed > 0 && <Pill text={`!${counts.failed} failed`} tone="red" data-id="element-1961" />}
          {counts.new === 0 && counts.updated === 0 && counts.failed === 0 && status !== 'running' && <span className="text-[12px] text-[#8a8680]/40" data-id="element-1962">no changes</span>}
          {status === 'running' && <span className="text-[12px] text-[#e8a84c]" data-id="element-1963">running…</span>}
        </div>
        <span className="text-[12px] text-[#8a8680]/40 shrink-0" data-id="element-1964">{formatDuration(durationMs)}</span>
        <ChevronRight size={14} className="text-[#8a8680]/30 shrink-0" data-id="element-1965" />
      </div>
    </button>;
}
function Pill({
  text,
  tone
}: {
  text: string;
  tone: 'teal' | 'muted' | 'red';
}) {
  const classes = tone === 'teal' ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : tone === 'red' ? 'bg-[#f47067]/15 text-[#f47067]' : 'bg-[#252530] text-[#8a8680]';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${classes}`} data-id="element-1966">{text}</span>;
}